import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

const fmtNum = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return v.toFixed(0);
  if (a >= 10) return v.toFixed(2);
  return v.toFixed(4);
};

const fmtPct = (v) => `${Math.round(v * 100)}%`;

// Distinct colors for compare mode (single mode just uses the first).
const SERIES_COLORS = ['#ff9800', '#42a5f5', '#66bb6a', '#ab47bc', '#ef5350'];

// Signed delta of a scenario value vs the baseline at the same bin, in the
// units the reader thinks in: probability points for classification goals,
// otherwise value + percent of baseline.
const deltaText = (scenario, base, probability, unit) => {
  const diff = scenario - base;
  if (probability) {
    const pts = Math.round(diff * 1000) / 10;
    return `${pts >= 0 ? '+' : ''}${pts} pts`;
  }
  let out = `${diff >= 0 ? '+' : ''}${fmtNum(diff)}${unit ? ` ${unit}` : ''}`;
  if (base !== 0) {
    const pct = (100 * diff) / Math.abs(base);
    out += ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`;
  }
  return out;
};

/**
 * Baseline vs scenario forecast paths (plan Step 15). Pure SVG — a dashed
 * baseline plus one or more solid scenario lines, one x-step per horizon bin.
 * Interactive: hovering (or touch-dragging) shows a crosshair with the
 * baseline value, each scenario value and its delta at that horizon step.
 *
 * Two modes, backward-compatible:
 *   • Single: pass ``fan`` (number[]) — draws one scenario line + a shaded
 *     delta band against the baseline.
 *   • Compare: pass ``series`` ([{ label, fan, color? }]) — overlays each
 *     scenario line against the shared baseline, with a legend (no band).
 *
 * For classification goals (trend / anomaly) the model "point" is a
 * probability path; pass ``probability`` to fix the y-domain to 0–100% and
 * format ticks as percentages.
 */
const ScenarioFanChart = ({
  baseline, fan, series, intervalSeconds = 3600, height = 240, probability = false,
  // The target's measurement unit ("$/MWh") — shown in the tooltip values
  // and the legend. Ignored in probability mode (values are %).
  unit = null,
  // Honest uncertainty bands ({p10: number[], p90: number[]}) when the
  // champion produces quantile paths (foundation backbones). Shaded behind
  // the lines in single-scenario mode; absent for classical champions.
  baselineBands = null,
  fanBands = null,
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState(null); // bin index or null
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Normalize to a series list. Single `fan` becomes one series; explicit
  // `series` wins. Assign colors where missing.
  const norm = useMemo(() => {
    let list = [];
    if (Array.isArray(series) && series.length) {
      list = series.filter((s) => s && Array.isArray(s.fan) && s.fan.length);
    } else if (Array.isArray(fan) && fan.length) {
      list = [{ label: 'scenario', fan }];
    }
    return list.map((s, i) => ({ ...s, color: s.color || SERIES_COLORS[i % SERIES_COLORS.length] }));
  }, [series, fan]);

  const compare = Array.isArray(series) && series.length > 1;

  // Bands only shade in single-scenario mode (compare stays readable).
  const showBands = !compare && !probability;
  const bandPair = (bands, n) => (
    showBands && Array.isArray(bands?.p10) && Array.isArray(bands?.p90)
    && bands.p10.length >= n && bands.p90.length >= n
      ? { lo: bands.p10, hi: bands.p90 } : null
  );

  const model = useMemo(() => {
    if (!baseline?.length || norm.length === 0) return null;
    let n = baseline.length;
    norm.forEach((s) => { n = Math.min(n, s.fan.length); });
    if (n < 1) return null;
    if (probability) {
      return { yMin: -0.02, yMax: 1.02, n };
    }
    const all = [...baseline.slice(0, n)];
    norm.forEach((s) => all.push(...s.fan.slice(0, n)));
    // Quantile bands stretch the domain so the shading never clips.
    [bandPair(baselineBands, n), bandPair(fanBands, n)].forEach((bp) => {
      if (bp) all.push(...bp.lo.slice(0, n), ...bp.hi.slice(0, n));
    });
    let yMin = Math.min(...all);
    let yMax = Math.max(...all);
    if (yMin === yMax) { yMin -= 1; yMax += 1; }
    const pad = (yMax - yMin) * 0.12;
    return { yMin: yMin - pad, yMax: yMax + pad, n };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline, norm, probability, baselineBands, fanBands, showBands]);

  const M = { top: 10, right: 12, bottom: 24, left: 56 };
  const W = Math.max(width, 320);
  const innerW = W - M.left - M.right;
  const innerH = height - M.top - M.bottom;
  const n = model?.n || 0;

  const x = useCallback(
    (i) => M.left + (innerW * i) / Math.max(n - 1, 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [innerW, n],
  );
  const y = useCallback(
    (v) => M.top + innerH * (1 - (v - model.yMin) / (model.yMax - model.yMin)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [innerH, model?.yMin, model?.yMax],
  );

  const binFromEvent = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg || n < 1) return null;
    const rect = svg.getBoundingClientRect();
    const clientX = evt.touches?.[0]?.clientX ?? evt.clientX;
    const mx = clientX - rect.left;
    const frac = (mx - M.left) / Math.max(innerW, 1);
    return Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerW, n]);

  const onMove = useCallback((evt) => {
    const i = binFromEvent(evt);
    if (i != null) setHover(i);
  }, [binFromEvent]);
  const onLeave = useCallback(() => setHover(null), []);

  if (!model) return null;

  // The hover index can go stale when the user switches to a scenario with
  // a shorter horizon while the component stays mounted — clamp it.
  const hov = hover != null ? Math.min(hover, model.n - 1) : null;

  const line = (vals) => vals.slice(0, model.n)
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');

  // Delta band only in single-scenario mode (keeps compare readable).
  const band = (!compare && norm.length === 1) ? [
    ...norm[0].fan.slice(0, model.n).map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`),
    ...baseline.slice(0, model.n).map((v, i) => `L${x(model.n - 1 - i).toFixed(1)},${y(baseline[model.n - 1 - i]).toFixed(1)}`),
    'Z',
  ].join(' ') : null;

  // Quantile envelope path (p10–p90) — shading under a line.
  const envelope = (bp) => {
    if (!bp) return null;
    return [
      ...bp.hi.slice(0, model.n).map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`),
      ...bp.lo.slice(0, model.n).map((_, i) => {
        const j = model.n - 1 - i;
        return `L${x(j).toFixed(1)},${y(bp.lo[j]).toFixed(1)}`;
      }),
      'Z',
    ].join(' ');
  };
  const baselineEnvelope = envelope(bandPair(baselineBands, model.n));
  const fanEnvelope = envelope(bandPair(fanBands, model.n));
  const hasEnvelopes = Boolean(baselineEnvelope || fanEnvelope);

  const fmtY = probability ? fmtPct : fmtNum;
  const fmtVal = probability
    ? ((v) => `${(v * 100).toFixed(1)}%`)
    : ((v) => (unit ? `${fmtNum(v)} ${unit}` : fmtNum(v)));
  const tickFracs = probability ? [0, 0.25, 0.5, 0.75, 1] : [0, 0.5, 1];
  const yTicks = tickFracs.map((t) => model.yMin + t * (model.yMax - model.yMin));
  const stepLabel = (i) => {
    const totalH = ((i + 1) * intervalSeconds) / 3600;
    return totalH >= 48 ? `+${Math.round(totalH / 24)}d` : `+${Math.round(totalH)}h`;
  };

  const hovBase = hov != null ? baseline[hov] : null;
  // Tooltip flips to the left of the crosshair past mid-chart.
  const tipOnLeft = hov != null && x(hov) > M.left + innerW * 0.55;

  return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
      <svg
        ref={svgRef} width={W} height={height}
        style={{ display: 'block', touchAction: 'pan-y', cursor: hov != null ? 'crosshair' : 'default' }}
        onMouseMove={onMove} onMouseLeave={onLeave}
        onTouchStart={onMove} onTouchMove={onMove} onTouchEnd={onLeave}
      >
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} stroke="currentColor" opacity={0.08} />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="currentColor" opacity={0.55}>
              {fmtY(v)}
            </text>
          </g>
        ))}
        {[0, Math.floor((model.n - 1) / 2), model.n - 1].map((i) => (
          <text key={i} x={x(i)} y={height - 6} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.5}>
            {stepLabel(i)}
          </text>
        ))}
        {baselineEnvelope && (
          <path d={baselineEnvelope} fill="currentColor" opacity={0.07} />
        )}
        {fanEnvelope && (
          <path d={fanEnvelope} fill={norm[0].color} opacity={0.1} />
        )}
        {band && <path d={band} fill={norm[0].color} opacity={0.14} />}
        <path d={line(baseline)} fill="none" stroke="currentColor" strokeWidth={1.75} opacity={0.65} strokeDasharray="5 3" />
        {norm.map((s) => (
          <path key={s.label} d={line(s.fan)} fill="none" stroke={s.color} strokeWidth={2.25} />
        ))}
        {hov != null && (
          <g pointerEvents="none">
            <line
              x1={x(hov)} x2={x(hov)} y1={M.top} y2={height - M.bottom}
              stroke="currentColor" opacity={0.35} strokeDasharray="2 2"
            />
            {hovBase != null && (
              <circle cx={x(hov)} cy={y(hovBase)} r={3.5} fill="currentColor" opacity={0.75} />
            )}
            {norm.map((s) => (
              s.fan[hov] != null && (
                <circle key={s.label} cx={x(hov)} cy={y(s.fan[hov])} r={4} fill={s.color} stroke="#000" strokeOpacity={0.35} />
              )
            ))}
          </g>
        )}
      </svg>

      {hov != null && (
        <Box
          sx={{
            position: 'absolute',
            top: M.top,
            left: tipOnLeft ? 'auto' : Math.min(x(hov) + 10, W - 180),
            right: tipOnLeft ? Math.max(W - x(hov) + 10, M.right) : 'auto',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
            px: 1.25, py: 0.75,
            pointerEvents: 'none',
            zIndex: 2,
            minWidth: 150,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            {stepLabel(hov)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            baseline {fmtVal(hovBase)}
          </Typography>
          {norm.map((s) => (
            s.fan[hov] != null && (
              <Typography key={s.label} variant="caption" sx={{ display: 'block', color: s.color }} noWrap>
                {compare ? `${s.label}: ` : ''}{fmtVal(s.fan[hov])}
                {hovBase != null && (
                  <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                    {deltaText(s.fan[hov], hovBase, probability, unit)}
                  </Box>
                )}
              </Typography>
            )
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 0.5, ml: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          ┄ baseline{probability ? ' (probability)' : (unit ? ` (${unit})` : '')}
        </Typography>
        {norm.map((s) => (
          <Typography key={s.label} variant="caption" sx={{ color: s.color }}>— {s.label}</Typography>
        ))}
        {hasEnvelopes && (
          <Typography variant="caption" color="text.disabled">
            shaded: p10–p90 forecast band
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ScenarioFanChart;
